/**
 * Shared tile-cache identity for Sprint 2 / Lighthouse playback sprint.
 * Cache keys must include montage, channel set, zoom, filter, and gain so
 * warm tiles from another view context are never reused incorrectly.
 */

export interface TileCacheIdentity {
  montageId?: string | null;
  channels?: readonly string[] | null;
  /** Viewer time_scale (seconds of EEG shown across the viewport). */
  timeScale?: number | null;
  /** High-pass cutoff in Hz (null/undefined => open / default). */
  highPassHz?: number | null;
  /** Low-pass cutoff in Hz (null/undefined => open / default). */
  lowPassHz?: number | null;
  /** Notch frequency in Hz when enabled. */
  notchHz?: number | null;
  /** Display sensitivity in μV/mm (gain). */
  sensitivityUvPerMm?: number | null;
}

const DEFAULT_MONTAGE = "_";
const DEFAULT_CHANNEL_HASH = "_";
const DEFAULT_ZOOM_BUCKET = "_";
const DEFAULT_FILTER_HASH = "_";
const DEFAULT_GAIN_BUCKET = "_";

/** FNV-1a 32-bit over a sorted channel list — stable and short. */
export function hashTileChannelSet(
  channels?: readonly string[] | null,
): string {
  if (!channels || channels.length === 0) {
    return DEFAULT_CHANNEL_HASH;
  }
  const sorted = Array.from(
    new Set(
      channels
        .map((c) => String(c || "").trim())
        .filter((c) => c.length > 0),
    ),
  ).sort((a, b) => a.localeCompare(b));
  if (sorted.length === 0) {
    return DEFAULT_CHANNEL_HASH;
  }
  let hash = 0x811c9dc5;
  const joined = sorted.join("\u0001");
  for (let i = 0; i < joined.length; i += 1) {
    hash ^= joined.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return (hash >>> 0).toString(16);
}

/**
 * Bucket zoom so tiny time_scale nudges do not explode cache cardinality.
 * Uses one decimal place in log2 space of seconds.
 */
export function bucketTileZoom(timeScale?: number | null): string {
  if (
    typeof timeScale !== "number" ||
    !Number.isFinite(timeScale) ||
    timeScale <= 0
  ) {
    return DEFAULT_ZOOM_BUCKET;
  }
  const bucket = Math.round(Math.log2(timeScale) * 10) / 10;
  return String(bucket);
}

function normalizeFilterHz(value?: number | null): string {
  if (typeof value !== "number" || !Number.isFinite(value) || value <= 0) {
    return "0";
  }
  return String(Math.round(value * 100) / 100);
}

/** Compact filter identity: hp|lp|notch */
export function hashTileFilterSet(identity?: TileCacheIdentity | null): string {
  if (!identity) {
    return DEFAULT_FILTER_HASH;
  }
  const hp = normalizeFilterHz(identity.highPassHz);
  const lp = normalizeFilterHz(identity.lowPassHz);
  const notch = normalizeFilterHz(identity.notchHz);
  if (hp === "0" && lp === "0" && notch === "0") {
    return DEFAULT_FILTER_HASH;
  }
  return `${hp}|${lp}|${notch}`;
}

/**
 * Bucket display gain (μV/mm) so tiny slider noise does not thrash the cache.
 */
export function bucketTileGain(sensitivityUvPerMm?: number | null): string {
  if (
    typeof sensitivityUvPerMm !== "number" ||
    !Number.isFinite(sensitivityUvPerMm) ||
    sensitivityUvPerMm <= 0
  ) {
    return DEFAULT_GAIN_BUCKET;
  }
  return String(Math.round(sensitivityUvPerMm * 10) / 10);
}

export function normalizeTileMontageId(
  montageId?: string | null,
): string {
  const trimmed = String(montageId || "").trim();
  return trimmed.length > 0 ? trimmed : DEFAULT_MONTAGE;
}

export function resolveTileCacheIdentity(
  identity?: TileCacheIdentity | null,
): {
  montageId: string;
  channelSetHash: string;
  zoomBucket: string;
  filterHash: string;
  gainBucket: string;
} {
  return {
    montageId: normalizeTileMontageId(identity?.montageId),
    channelSetHash: hashTileChannelSet(identity?.channels),
    zoomBucket: bucketTileZoom(identity?.timeScale),
    filterHash: hashTileFilterSet(identity),
    gainBucket: bucketTileGain(identity?.sensitivityUvPerMm),
  };
}

export function buildTileCacheKey(params: {
  recordingId: string;
  startMs: number;
  endMs: number;
  level: number;
  speedBucket: number;
  transportMode: "play" | "inspect";
  identity?: TileCacheIdentity | null;
}): string {
  const resolved = resolveTileCacheIdentity(params.identity);
  return [
    params.recordingId,
    params.startMs,
    params.endMs,
    params.level,
    params.speedBucket,
    params.transportMode,
    resolved.montageId,
    resolved.channelSetHash,
    resolved.zoomBucket,
    resolved.filterHash,
    resolved.gainBucket,
  ].join("_");
}
